/**
 * Recognising a security challenge that an EDGE in front of ERPat answered
 * with, instead of ERPat itself.
 *
 * Pure module — no `import.meta.env`, no Vite, no DOM- or Node-only types — so
 * the app (`sessionFetch`), `vite.config.ts` (the dev/preview proxy) and the
 * Vitest suite all share one definition. It exists because of a real incident
 * (2026-09-30): the ERPat zone went behind a Cloudflare managed challenge, and
 * EVERY request — `GET /v1/api/ping`, the CORS `OPTIONS` preflight, the realtime
 * gateway — started answering `403 text/html` "Just a moment…".
 *
 * A challenge is a web page for a person to solve. A `fetch()`, a CORS
 * preflight (which never carries cookies), a Capacitor WebView calling
 * cross-origin and a Node proxy cannot solve one, so the request never reaches
 * ERPat at all. That last part is what the app got wrong: the SDK turns the
 * HTML body into `invalid_response` / 403, and a 403 reads everywhere as an
 * ANSWER — "not available for your account", "wrong password", "your session
 * was rejected, sign out". None of those were ever said by the server.
 *
 * What is detectable, and where: Cloudflare marks every challenge page with
 * `cf-mitigated: challenge` ("challenge" is the only value it documents). The
 * header is readable only when the response itself is — i.e. same-origin (the
 * dev proxy, the verify preview). Cross-origin (the deployed PWA, the native
 * shells) the preflight is challenged first and `fetch` rejects with an opaque
 * TypeError, so nothing here can see it. The fix for THAT is the operator's
 * WAF Skip rule — docs/pwa-runbook.md → "Edge: Cloudflare in front of
 * dash.erpat.app and pushit.erpat.app".
 */
import { isLoopbackHostname } from './api-base-url';

/** The response header Cloudflare sets on every challenge page. */
export const CF_MITIGATED_HEADER = 'cf-mitigated';

/**
 * What a request the edge intercepted rejects with, as its `message`.
 *
 * The message IS the discriminator: `@erpat/sdk` wraps a rejected fetch as
 * `ErpatApiError('network_error', err.message, 0)` and keeps nothing else, so a
 * fixed string is the only thing that survives to `isSecurityChallenge()` in
 * services/api/http.ts — the same channel `SESSION_CANCELLED_MESSAGE` uses.
 * Worded for a person because it is shown to one.
 *
 * It lives here rather than beside its sibling in erpat-client.ts because that
 * module is mocked with partial factories across the suite; a pure util never is.
 */
export const SECURITY_CHALLENGE_MESSAGE =
  "The server's security check is blocking the app. Please contact your administrator.";

/**
 * `challenge` — the documented marker was present: Cloudflare says so itself.
 * `block` — no marker, but an HTML 403 that came through Cloudflare: usually its
 * block page (a WAF block / error 1020); an HTML 403 the ORIGIN wrote matches
 * too, because Cloudflare sets `server: cloudflare` on every proxied response.
 * So `block` is an inference, not a verdict — {@link formatChallengeWarning}
 * words it that way.
 */
export type EdgeChallengeKind = 'challenge' | 'block';

/**
 * Reads one response header by (lower-case) name. Shaped to accept both a
 * browser `Headers.get()` (`string | null`) and a Node `IncomingMessage.headers`
 * lookup (`string | string[] | undefined`) without importing either type.
 */
export type HeaderReader = (name: string) => string | readonly string[] | null | undefined;

function headerText(value: string | readonly string[] | null | undefined): string {
  const first = typeof value === 'string' ? value : (value?.[0] ?? '');
  return first.trim().toLowerCase();
}

/**
 * Classify a response as an edge challenge, or `null` when it is anything else.
 *
 *  1. `cf-mitigated: challenge` — at ANY status; this is Cloudflare's signal.
 *  2. Fallback (`block`): a `403` whose body is `text/html` and whose `server`
 *     is `cloudflare` — usually a block page that carries no marker; an HTML
 *     403 the origin wrote matches too (see {@link EdgeChallengeKind}).
 *
 * Deliberately NOT matched: ERPat's own JSON 403 (a real answer), the 200 HTML
 * page the `check_database` hook redirects an under-migrated tenant to (that
 * stays `invalid_response` and keeps its "database may need updating" copy),
 * and an HTML 403 from any server that is not Cloudflare.
 */
export function detectEdgeChallenge(status: number, header: HeaderReader): EdgeChallengeKind | null {
  if (headerText(header(CF_MITIGATED_HEADER)) === 'challenge') return 'challenge';
  if (
    status === 403 &&
    headerText(header('content-type')).startsWith('text/html') &&
    headerText(header('server')) === 'cloudflare'
  ) {
    return 'block';
  }
  return null;
}

/**
 * The error an intercepted request rejects with. A plain `Error` on purpose —
 * NOT an `AbortError`: every "was this aborted?" check in the app treats those
 * as a cancellation and stays silent, which would leave pages blank and the
 * sign-in pill stuck on "Checking system…".
 */
export function securityChallengeError(): Error {
  return Object.assign(new Error(SECURITY_CHALLENGE_MESSAGE), { name: 'SecurityChallengeError' });
}

/**
 * True when `err` is a request the edge intercepted — as raised by
 * `sessionFetch()`, or after the SDK / an upload helper wrapped it (both keep
 * the message and nothing else, which is why the message is what is compared).
 *
 * Not offline, not a refusal, and never a reason to end the session: ERPat was
 * not asked. Structural on purpose (`ErpatApiError` extends `Error`), so this
 * module needs no SDK import and stays usable from specs that mock the
 * transport modules.
 */
export function isSecurityChallenge(err: unknown): boolean {
  return err instanceof Error && err.message === SECURITY_CHALLENGE_MESSAGE;
}

// ── Dev / preview proxy diagnostics (vite.config.ts) ─────────────────────────

/** The path prefixes the dev/preview server forwards to the backend. */
export const PROXIED_PATH_PREFIXES: readonly string[] = ['/v1/api', '/api', '/uploads', '/files', '/storage'];

export interface ProxyTargetDescription {
  /** `off` — nothing is forwarded; `local` — a loopback ERPat; `remote` — anything else. */
  kind: 'off' | 'local' | 'remote';
  /** One terminal line, ready to print under Vite's own URL banner. */
  line: string;
}

/**
 * Say, in one line, which backend the dev/preview server forwards the API
 * paths to. Nothing in the app shows this, and a `.env` pointed at production
 * looks exactly like one pointed at a local ERPat until a sign-in hits a real
 * account.
 */
export function describeProxyTarget(target: string | undefined): ProxyTargetDescription {
  const value = (target ?? '').trim();
  if (value === '') {
    return {
      kind: 'off',
      line: '[syntry] API proxy: OFF — no proxy target is configured, so this server forwards nothing.',
    };
  }

  const head = `[syntry] API proxy: ${PROXIED_PATH_PREFIXES.join(' ')} → ${value}`;
  let hostname: string;
  try {
    hostname = new URL(value).hostname;
  } catch {
    return { kind: 'remote', line: `${head}  (not a URL this server can parse — check the proxy target)` };
  }

  if (isLoopbackHostname(hostname)) {
    return { kind: 'local', line: `${head}  (a local ERPat)` };
  }
  return {
    kind: 'remote',
    line: `${head}  (REMOTE — not a local ERPat: sign-ins and writes reach real accounts)`,
  };
}

/**
 * The full terminal warning for one flagged proxied response.
 *
 * The two kinds are worded differently because they are KNOWN differently.
 * `challenge` carries Cloudflare's own marker, so its text says flatly that
 * ERPat never saw the request (CLAUDE.md quotes its first line — keep that text
 * byte for byte). `block` is inferred from `server: cloudflare`, which
 * Cloudflare stamps on every response it proxies: an HTML 403 the origin wrote
 * (an nginx deny, a file-permission page) has exactly the same shape. Claiming
 * "ERPat never saw this request" for that one sends a developer to the WAF
 * runbook for something the origin did — so the `block` text names both
 * possible authors and what follows from each, and asserts neither.
 */
export function formatChallengeWarning(
  kind: EdgeChallengeKind,
  method: string,
  url: string,
  target: string,
): string {
  if (kind === 'block') {
    return [
      `[syntry proxy] ${method} ${url} → ${target} answered with an HTML 403 served through Cloudflare ` +
        "(no cf-mitigated header — a block page, or the origin's own 403).",
      "  If it is Cloudflare's block page, ERPat never saw this request — the fix is the WAF Skip rule in " +
        'docs/pwa-runbook.md → "Edge: Cloudflare in front of dash.erpat.app and pushit.erpat.app".',
      '  If the origin wrote it, it is an ordinary 403 for that path — no edge rule will change it.',
    ].join('\n');
  }

  return [
    `[syntry proxy] ${method} ${url} → ${target} answered with a Cloudflare challenge (cf-mitigated: challenge, text/html "Just a moment…").`,
    '  ERPat never saw this request. Neither this proxy nor a fetch() in the browser can pass a challenge,',
    '  so do not debug the backend: the app will read "Blocked by the server\'s security check".',
    '  Fix (zone operator): the WAF Skip rule in docs/pwa-runbook.md → "Edge: Cloudflare in front of',
    '  dash.erpat.app and pushit.erpat.app".',
    '  Meanwhile: point the proxy target at a local ERPat (http://localhost:8000).',
  ].join('\n');
}

/** How long repeat challenges from one target are folded into a single line. */
export const CHALLENGE_FOLD_WINDOW_MS = 30_000;

export interface ChallengeReporter {
  report(kind: EdgeChallengeKind, method: string, url: string, target: string): void;
}

/**
 * Report challenged responses without burying the terminal: a page load fires
 * dozens of API calls and every one of them is challenged. The first response
 * from a target logs the full warning; later ones are counted and folded into
 * one line, at most once per {@link CHALLENGE_FOLD_WINDOW_MS}. No timer — the
 * fold line is written when the next challenged response arrives, so nothing
 * here keeps a process alive.
 */
export function createChallengeReporter(
  log: (line: string) => void,
  now: () => number = Date.now,
): ChallengeReporter {
  const seen = new Map<string, { loggedAt: number; folded: number }>();

  return {
    report(kind, method, url, target) {
      const state = seen.get(target);
      if (!state) {
        seen.set(target, { loggedAt: now(), folded: 0 });
        log(formatChallengeWarning(kind, method, url, target));
        return;
      }

      state.folded += 1;
      const elapsed = now() - state.loggedAt;
      if (elapsed < CHALLENGE_FOLD_WINDOW_MS) return;

      log(
        `[syntry proxy] …and ${state.folded} more challenged response${state.folded === 1 ? '' : 's'} from ` +
          `${target} in the last ${Math.round(elapsed / 1000)} s (same cause).`,
      );
      state.loggedAt = now();
      state.folded = 0;
    },
  };
}
